/**
 * messaging-visibility — which Messaging rail tabs are shown, and where the
 * rail item lands.
 *
 * 2026-09-04 (operator): Messages and Workflows are HIDDEN from the rail while
 * the block-editor convergence is in flight. (Message delivery was hidden in the
 * same pass and was UN-HIDDEN 2026-09-14 at operator request — see below.)
 * Hidden, not removed — the routes stay registered, the screens stay mounted for a direct
 * link, the counts hook still runs, and no data path changes.
 *
 * ── TURNING THEM BACK ON ──────────────────────────────────────────────────
 *
 * In the running app, no code change, no rebuild:
 *
 *     ?messagingTabs=all       show every tab (sticks until you turn it off)
 *     ?messagingTabs=hidden    back to the default hide
 *
 * e.g. …/#/messaging?messagingTabs=all — the choice is remembered in
 * localStorage under `jrni.messagingTabs`, so it survives a reload and you set
 * it once. It is a per-browser override, not a tenant setting: nobody else sees
 * your choice.
 *
 * Permanently, in code: empty `DEFAULT_HIDDEN` below (or delete one id from
 * it). That changes the default for everyone and is the real un-hide.
 *
 * Its own module, with NO imports, because two graphs need it: the tab builder
 * (`messaging-tabs.ts`) and the shell's rail-route map (`shell/chrome-rail.tsx`).
 * Putting it in either would make the shell import a feature screen's data
 * hooks, or the feature import the shell's chrome — a cycle for a constant.
 */

/** The default: which tab ids are hidden when nobody has overridden anything.
 *
 * `messages`, `flows`, and `apptcomms` stay hidden by default. Message Builder,
 * Settings, and Message delivery retain their independent routes and capability
 * gates. The retired automatic-message catalogue is no longer a route or tab.
 */
const DEFAULT_HIDDEN = ["messages", "flows", "apptcomms"];

const STORE_KEY = "jrni.messagingTabs";

/** `all` = show everything · `hidden` = use DEFAULT_HIDDEN · null = no override.
 *  Read from the URL first (that is how you SET it) and persisted, so the next
 *  page load without the param still honours the choice. */
function override(): "all" | "hidden" | null {
  if (typeof window === "undefined") return null;
  let val: string | null = null;
  try {
    /* The param may sit in the query OR after the hash route, depending on how
       the link was pasted — check both rather than making the user care. */
    const search = window.location.search + "&" + window.location.hash.replace(/^[^?]*\??/, "");
    val = new URLSearchParams(search).get("messagingTabs");
    if (val === "all" || val === "hidden") window.localStorage.setItem(STORE_KEY, val);
    else val = window.localStorage.getItem(STORE_KEY);
  } catch { /* private mode / blocked storage — fall through to the default */ }
  return val === "all" || val === "hidden" ? val : null;
}

/** Hidden tab ids, honouring the override. A Set, so callers read as before. */
export const HIDDEN_TABS: Set<string> = new Set(override() === "all" ? [] : DEFAULT_HIDDEN);

/** Every Messaging tab, in rail order, with the route each one opens. */
const TAB_ROUTES: [string, string][] = [
  ["messages", "messaging"],
  ["flows", "messaging.flows"],
  ["delivery", "messaging.delivery"],
  ["apptcomms", "messaging.appointments"],
  ["builder", "messaging.builder"],
  ["settings", "messaging.settings"],
];

/** The rail item's destination — the first tab an operator can actually see.
 *  Without this, hiding `messages` lands Messaging on a screen whose tab is not
 *  in the rail: nothing highlighted and no way back. Falls through to
 *  `messaging` if everything is hidden, so the route map is never empty. */
export function messagingLandingRoute(): string {
  const first = TAB_ROUTES.find(([id]) => !HIDDEN_TABS.has(id));
  return first ? first[1] : "messaging";
}
